import { t } from "i18next";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { Price } from "../api/models/prices.models";
import { SupClass } from "../api/models/supClass.models";
import { AccordionRow } from "../components/summaryAccordion";
import { getClassLabelFromAkeneoObject } from "./akeneoUtils";
import { getRowPrice, PriceType } from "./priceUtils";
import {
  isInteger,
  getCombinedWeight,
  getEcomodMaterials,
  getCombinedSupClassWeight,
  getSupMaterials,
} from "./rowUtils";
import { Report, ReportType } from "../api/models/report.models";
import { Locale } from "../types/ui-types";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";

export const createSummaryData = (
  reportData: Report,
  ecomodClasses: EcomodClass[],
  supClasses: SupClass[],
  pricesData: Price[],
  locale: Locale,
) => {
  const mainEcomodClasses = ecomodClasses?.filter((ecomodClass) =>
    isInteger(ecomodClass.code as string),
  );

  const newConsumerRows: AccordionRow[] = [];
  const newBusinessRows: AccordionRow[] = [];
  const newSupRows: AccordionRow[] = [];

  const isRinkiReport =
    reportData.reportType === ReportType.DETAILED ||
    reportData.reportType === ReportType.LIGHT;

  mainEcomodClasses?.forEach((ecomodClass) => {
    const {
      totalWeight: consumerCombinedWeight,
      recycledWeight: consumerRecycledWeight,
    } = getCombinedWeight(ecomodClass.code as string, reportData, "consumer");

    const nonRecycledConsumerWeight =
      consumerRecycledWeight > 0 && consumerCombinedWeight > 0
        ? consumerCombinedWeight - consumerRecycledWeight
        : consumerCombinedWeight;

    const ecomodItem = ecomodClasses.find(
      (item) => item.code === ecomodClass.code,
    );

    const ecomodMaterialsConsumer = getEcomodMaterials(
      ecomodClass.code as string,
      ecomodClasses,
      reportData,
      "consumer",
      locale,
      pricesData,
    );

    // If any material has sub materials reported, sum their prices instead of calculating price from combined weight
    const consumerPrice = ecomodMaterialsConsumer.some(
      (material) => !!material.combinedWeight,
    )
      ? ecomodMaterialsConsumer.reduce(
        (total, material) => total + (material.price || 0),
        0,
      )
      : getRowPrice(
        nonRecycledConsumerWeight,
        pricesData,
        ecomodItem?.id,
        undefined,
        PriceType.ForConsumer,
      );

    const newRow: AccordionRow = {
      key: ecomodClass.code + "consumer",
      type: "consumer",
      label: getClassLabelFromAkeneoObject(locale, ecomodClass) as string,
      combinedWeight: consumerCombinedWeight,
      recycledWeight: consumerRecycledWeight,
      price: consumerPrice,
      ecomodMaterials: ecomodMaterialsConsumer,
    };
    newConsumerRows.push(newRow);

    const {
      totalWeight: businessCombinedWeight,
      recycledWeight: businessRecycledWeight,
    } = getCombinedWeight(ecomodClass.code as string, reportData, "business");

    const nonRecycledBusinessWeight =
      businessRecycledWeight > 0 && businessCombinedWeight > 0
        ? businessCombinedWeight - businessRecycledWeight
        : businessCombinedWeight;

    const ecomodMaterialsBusiness = getEcomodMaterials(
      ecomodClass.code as string,
      ecomodClasses,
      reportData,
      "business",
      locale,
      pricesData,
    );

    const businessPrice = ecomodMaterialsBusiness.some(
      (material) => !!material.combinedWeight,
    )
      ? ecomodMaterialsBusiness.reduce(
        (total, material) => total + (material.price || 0),
        0,
      )
      : getRowPrice(
        nonRecycledBusinessWeight,
        pricesData,
        ecomodItem?.id,
        undefined,
        PriceType.ForBusiness,
      );

    const newRowBusiness: AccordionRow = {
      key: ecomodClass.code + "business",
      type: "business",
      label: getClassLabelFromAkeneoObject(locale, ecomodClass) as string,
      combinedWeight: businessCombinedWeight,
      recycledWeight: businessRecycledWeight,
      ecomodMaterials: ecomodMaterialsBusiness,
      price: businessPrice,
    };
    newBusinessRows.push(newRowBusiness);
  });

  // Sup plastic
  const supPlasticWeight = getCombinedSupClassWeight(
    "2",
    reportData,
    isRinkiReport,
  );

  const supPlasticMaterials = getSupMaterials(
    "2",
    supClasses,
    reportData,
    locale,
    pricesData,
  );

  const supPlasticPrice = supPlasticMaterials.reduce(
    (total, material) => total + (material.price || 0),
    0,
  );

  newSupRows.push({
    key: "supPlastic",
    type: "consumer",
    label: t("label-sup-plastic"),
    combinedWeight: supPlasticWeight,
    recycledWeight: 0,
    price: supPlasticPrice,
    ecomodMaterials: supPlasticMaterials,
  });

  // Sup paper
  const supPaperWeight = getCombinedSupClassWeight(
    "1",
    reportData,
    isRinkiReport,
  );

  const supPaperMaterials = getSupMaterials(
    "1",
    supClasses,
    reportData,
    locale,
    pricesData,
  );

  const supPaperPrice = supPaperMaterials.reduce(
    (total, material) => total + (material.price || 0),
    0,
  );

  newSupRows.push({
    key: "supPaper",
    type: "consumer",
    label: t("label-sup-paper"),
    combinedWeight: supPaperWeight,
    recycledWeight: 0,
    price: supPaperPrice,
    ecomodMaterials: supPaperMaterials,
  });

  // Sup other
  const supOtherWeight = getCombinedSupClassWeight(
    null,
    reportData,
    isRinkiReport,
  );

  const supOtherMaterials = getSupMaterials(
    null,
    supClasses,
    reportData,
    locale,
    pricesData,
  );

  const supOtherPrice = supOtherMaterials.reduce(
    (total, material) => total + (material.price || 0),
    0,
  );

  newSupRows.push({
    key: "supOther",
    type: "consumer",
    label: t("header-other-materials"),
    combinedWeight: supOtherWeight,
    recycledWeight: 0,
    price: supOtherPrice,
    ecomodMaterials: supOtherMaterials,
  });

  return {
    consumer: newConsumerRows,
    business: newBusinessRows,
    sup: newSupRows,
  };
};

export const getMatchingReport = (
  prevYearReports: Report[] | null | undefined,
  currentReport: Report | undefined,
): Report | null => {
  // If no previous year reports, return null
  if (!prevYearReports) {
    return null;
  }

  // If current report is monthly, find previous year same month report
  if (currentReport?.month && !currentReport?.quarter) {
    const month = currentReport.month as number;
    const sameMonthReport =
      prevYearReports.find(
        (report) => report.month === month && !report.quarter,
      ) || null;

    // If not found, return previous year yearly report
    if (!sameMonthReport) {
      return (
        prevYearReports.find((report) => !report.month && !report.quarter) ||
        null
      );
    }

    return sameMonthReport;
  }

  // If current report is quarterly, find previous year same quarter report
  if (currentReport?.quarter && !currentReport?.month) {
    const quarter = currentReport.quarter as number;
    const sameQuarterReport =
      prevYearReports.find(
        (report) => report.quarter === quarter && !report.month,
      ) || null;

    // If not found, return previous year yearly report
    if (!sameQuarterReport) {
      return (
        prevYearReports.find((report) => !report.month && !report.quarter) ||
        null
      );
    }

    return sameQuarterReport;
  }

  // If current report is yearly, find previous year yearly report
  if (!currentReport?.month && !currentReport?.quarter) {
    return (
      prevYearReports.find((report) => !report.month && !report.quarter) || null
    );
  }

  return null;
};

export const getChangeComparedToPrevYear = (
  current: number,
  previous: number | undefined,
  dividePreviousYearBy: number | null,
  multiplyPreviousYearBy: number | null,
): { weightChange: number; percentageChange: number } => {
  if (previous === undefined || previous === 0) {
    return { weightChange: 0, percentageChange: 0 };
  }

  const adjustedPrevious = dividePreviousYearBy
    ? previous / dividePreviousYearBy
    : multiplyPreviousYearBy
      ? previous * multiplyPreviousYearBy
      : previous;

  const weightChange = current - adjustedPrevious;
  const percentageChange = (weightChange / adjustedPrevious) * 100;

  return { weightChange, percentageChange };
};

export const getChangePercentageComparedToPrevYear = (
  current: number,
  previous: number | undefined,
  dividePreviousYearBy: number | null,
  multiplyPreviousYearBy: number | null,
): string => {
  if (previous === undefined || previous === 0) {
    return "-";
  }

  const { percentageChange } = getChangeComparedToPrevYear(
    current,
    previous,
    dividePreviousYearBy,
    multiplyPreviousYearBy,
  );
  const sign = percentageChange > 0 ? "+" : "";
  return `${sign}${percentageChange.toFixed(2)} %`;
};

export const getChangeErrors = (
  currentYearData: AccordionRow[],
  dividePreviousYearBy: number | null,
  multiplyPreviousYearBy: number | null,
  prevYearData?: AccordionRow[] | null,
  categoryLabel?: string,
): string[] => {
  const errors: string[] = [];

  if (!prevYearData) {
    return errors;
  }

  // Appends the category (e.g. consumer/business) so same material names from
  // different categories can be told apart in the combined error list.
  const withCategory = (label: string) =>
    categoryLabel ? `${label} (${categoryLabel})` : label;

  currentYearData.forEach((currentRow) => {
    const prevRow = prevYearData.find((row) => row.key === currentRow.key);

    if (prevRow) {
      const { weightChange, percentageChange } = getChangeComparedToPrevYear(
        currentRow.combinedWeight || 0,
        prevRow.combinedWeight,
        dividePreviousYearBy,
        multiplyPreviousYearBy,
      );

      // Check if change is at least 5000kg AND at least 10% and no ecomod materials
      if (
        Math.abs(weightChange) >= 5000 &&
        Math.abs(percentageChange) >= 10 &&
        currentRow.ecomodMaterials.every(
          (ecomod) => ecomod.combinedWeight === 0,
        )
      ) {
        errors.push(withCategory(currentRow.label));
      }
    }

    currentRow.ecomodMaterials?.forEach((material) => {
      const prevMaterial = prevYearData
        .find((row) => row.key === currentRow.key)
        ?.ecomodMaterials?.find((mat) => mat.key === material.key);

      if (prevMaterial) {
        const { weightChange, percentageChange } = getChangeComparedToPrevYear(
          material.combinedWeight || 0,
          prevMaterial.combinedWeight,
          dividePreviousYearBy,
          multiplyPreviousYearBy,
        );

        // Check if change is at least 5000kg AND at least 10%
        if (
          Math.abs(weightChange) >= 5000 &&
          Math.abs(percentageChange) >= 10
        ) {
          errors.push(
            withCategory(
              `${currentRow.label} - ${material.label || t("label-unknown-material")}`,
            ),
          );
        }
      }
    });
  });

  // Check for items in previous year that no longer exist in current year
  prevYearData.forEach((prevRow) => {
    const currentRow = currentYearData.find((row) => row.key === prevRow.key);

    if (
      !currentRow &&
      prevRow.combinedWeight &&
      Math.abs(prevRow.combinedWeight) > 5000
    ) {
      errors.push(
        withCategory(`${prevRow.label} (${t("removed-from-current-year")})`),
      );
    }
  });

  return errors;
};

export const getReportingCycle = (reportData: Report) => {
  if (reportData?.month && isInteger(reportData.month.toString())) {
    return ReportingCycleEnum.MONTHLY;
  }

  if (reportData?.quarter && isInteger(reportData.quarter.toString())) {
    return ReportingCycleEnum.QUARTERLY;
  }

  return ReportingCycleEnum.YEARLY;
};

export const getDivideAmount = (
  prevReportingCycle: ReportingCycleEnum,
  currentReportingCycle: ReportingCycleEnum,
) => {
  if (
    currentReportingCycle === ReportingCycleEnum.QUARTERLY &&
    prevReportingCycle === ReportingCycleEnum.YEARLY
  ) {
    return 4;
  }

  if (
    currentReportingCycle === ReportingCycleEnum.MONTHLY &&
    prevReportingCycle === ReportingCycleEnum.YEARLY
  ) {
    return 12;
  }

  if (
    currentReportingCycle === ReportingCycleEnum.MONTHLY &&
    prevReportingCycle === ReportingCycleEnum.QUARTERLY
  ) {
    return 3;
  }

  return null;
};

export const getMultiplyAmount = (
  prevReportingCycle: ReportingCycleEnum,
  currentReportingCycle: ReportingCycleEnum,
) => {
  if (
    currentReportingCycle === ReportingCycleEnum.YEARLY &&
    prevReportingCycle === ReportingCycleEnum.QUARTERLY
  ) {
    return 4;
  }

  if (
    currentReportingCycle === ReportingCycleEnum.YEARLY &&
    prevReportingCycle === ReportingCycleEnum.MONTHLY
  ) {
    return 12;
  }

  if (
    currentReportingCycle === ReportingCycleEnum.QUARTERLY &&
    prevReportingCycle === ReportingCycleEnum.MONTHLY
  ) {
    return 3;
  }

  return null;
};
